import { floorDisplayName } from "@uzverse/contracts";
import { Avatar, ErrorState, Icon, Loading } from "@uzverse/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { OrgAgent, OrgCompany } from "../api/types.js";
import { CompanyLogo } from "../brand/CompanyLogo.js";
import { useCommonT } from "../components/common.js";
import { useSession } from "../state/session.js";
import { useLoad } from "../state/useLoad.js";

const usd = (n: number) => `$${n.toFixed(2)}`;

function counts(c: OrgCompany) {
  const floors = c.buildings.flatMap((b) => b.floors);
  return {
    buildings: c.buildings.length,
    floors: floors.length,
    agents: floors.reduce((n, f) => n + f.agents.length, 0),
  };
}

function AgentRow({ agent }: { agent: OrgAgent }) {
  const { t } = useTranslation("web");
  const { t: tb } = useTranslation("brand");
  const tone = agent.status === "working" ? "dot-busy" : agent.status === "idle" ? "dot-ok" : "";
  return (
    <li className="agent-row">
      <Avatar name={agent.name} size={24} />
      <span className="grow">
        {agent.name}
        {agent.title ? <span className="muted small"> · {agent.title}</span> : null}
      </span>
      <span className={`chip${agent.kind === "lead" ? " chip-ask" : ""}`}>
        {agent.roleKey === "architect" || agent.roleKey === "interior_designer"
          ? tb(`role.${agent.roleKey}`)
          : t(`companies.${agent.kind}`)}
      </span>
      <span className="muted small agent-status">
        <span className={`dot ${tone}`} />
        {t(`companies.status.${agent.status}`)}
      </span>
    </li>
  );
}

function BudgetMeter({ budget }: { budget: NonNullable<OrgCompany["budget"]> }) {
  const { t } = useTranslation("web");
  if (budget.limitUsd === null) {
    return (
      <span className="muted small tnum">
        {t("context.budgetOpen", { used: usd(budget.usedUsd) })}
      </span>
    );
  }
  const pct = Math.min(100, (budget.usedUsd / (budget.limitUsd || 1)) * 100);
  return (
    <div className="stack-sm">
      <span className="muted small tnum">
        {t("context.budgetOf", { used: usd(budget.usedUsd), limit: usd(budget.limitUsd) })}
      </span>
      <div className="meter" aria-hidden="true">
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function CompaniesPage() {
  const { t } = useTranslation("web");
  const { t: tb } = useTranslation("brand");
  const locale = useTranslation("web").i18n.language.startsWith("tr") ? "tr" : "en";
  const tc = useCommonT();
  const navigate = useNavigate();
  const { services } = useSession();
  const org = useLoad(() => services.api.listOrganization());
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const companies = org.data;
  const selected = companies?.find((c) => c.id === selectedId) ?? companies?.[0];

  return (
    <section aria-labelledby="companies-title" className="page">
      <header className="page-head">
        <div>
          <h1 id="companies-title">{t("companies.title")}</h1>
          <p className="muted">{t("companies.intro")}</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void navigate("/", { state: { prompt: t("companies.setupPrompt") } })}
        >
          <Icon name="sparkles" size={16} />
          {t("companies.setup")}
        </button>
      </header>

      {org.error ? (
        <ErrorState t={tc} message={t("companies.unavailable")} onRetry={org.reload} />
      ) : !companies ? (
        <Loading t={tc} />
      ) : companies.length === 0 ? (
        <div className="empty-hero card rise">
          <Icon name="building" size={28} />
          <h2>{t("companies.empty")}</h2>
          <p className="muted">{t("companies.emptyHint")}</p>
        </div>
      ) : (
        <div className="split-pane">
          <ul className="plain company-list" aria-label={t("companies.select")}>
            {companies.map((c) => {
              const n = counts(c);
              const active = c.id === selected?.id;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`company-card card-hover${active ? " is-active" : ""}`}
                    aria-pressed={active}
                    onClick={() => setSelectedId(c.id)}
                  >
                    <CompanyLogo name={c.name} branding={c.branding} size={36} />
                    <span className="grow">
                      <strong>{c.name}</strong>
                      {c.demo ? <span className="chip">{tb("demo")}</span> : null}
                      <span className="muted small block">
                        {t("companies.buildings", { count: n.buildings })} ·{" "}
                        {t("companies.floors", { count: n.floors })} ·{" "}
                        {t("companies.agents", { count: n.agents })}
                      </span>
                    </span>
                    <Icon name="chevronRight" size={16} />
                  </button>
                </li>
              );
            })}
          </ul>

          {selected && (
            <div className="company-detail stack-lg rise" key={selected.id}>
              <div
                className="row row-between company-banner"
                style={
                  selected.branding
                    ? { borderLeft: `4px solid ${selected.branding.colors.primary}` }
                    : undefined
                }
              >
                <h2 className="row">
                  {selected.branding ? (
                    <img
                      className="company-wordmark"
                      src={selected.branding.logo.light}
                      alt={selected.name}
                      height={40}
                    />
                  ) : (
                    selected.name
                  )}
                  {selected.demo ? <span className="chip">{tb("demo")}</span> : null}
                  {selected.branding?.website ? (
                    <a
                      className="muted small"
                      href={selected.branding.website}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {selected.branding.website.replace(/^https:\/\//, "")}
                    </a>
                  ) : null}
                </h2>
                {selected.budget && (
                  <div className="budget-box">
                    <span className="eyebrow">{t("companies.budget")}</span>
                    <BudgetMeter budget={selected.budget} />
                  </div>
                )}
              </div>
              {selected.buildings.map((b) => (
                <article key={b.id} className="card building">
                  <h3 className="row">
                    <Icon name="cube" size={16} />
                    {b.name}
                    <span className="muted small">
                      {t("companies.floors", { count: b.floors.length })}
                    </span>
                  </h3>
                  {b.floors.length === 0 ? (
                    <p className="muted small">{t("companies.noFloors")}</p>
                  ) : (
                    <ul className="plain stack">
                      {b.floors.map((f) => (
                        <li key={f.id} className="floor">
                          <div className="floor-head">
                            <Icon name="layers" size={14} />
                            <strong>
                              {floorDisplayName({ ...f, level: f.level ?? 0 }, locale)}
                            </strong>
                            {f.level !== undefined ? (
                              <span className="muted small">
                                {f.level < 0
                                  ? tb("basement", { level: Math.abs(f.level) })
                                  : tb("level", { level: f.level })}
                              </span>
                            ) : null}
                            <span className="muted small">
                              {t("companies.agents", { count: f.agents.length })}
                            </span>
                          </div>
                          {f.agents.length === 0 ? (
                            <p className="muted small">{t("companies.noAgents")}</p>
                          ) : (
                            <ul className="plain agent-list">
                              {f.agents.map((a) => (
                                <AgentRow key={a.id} agent={a} />
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
